import React from "react";
import PropTypes from "prop-types";
import ImageCropped from "views_shared/app/javascript/components/ImageCropped";

const AVATAR_SIZE = 45;

/**
 * ListRow — one row of a profile page's divider list: a leading avatar (or an arbitrary glyph),
 * a linked-or-plain title, an optional "Title at Company" subtitle, and optional chip and action
 * slots.
 *
 * The enclosing list owns the `<ul>`, the `<li>`s, their order and the divider between them; this
 * component renders one row's content and nothing else, because only the list knows which of its
 * heterogeneous children (rows, placeholders, a scroll trigger) is last.
 *
 * Model-agnostic — plain display props in, no Id semantics.
 *
 * @param {Object} props
 * @param {React.ReactNode} [props.leading] - Replaces the avatar in the leading slot (e.g. a neutral
 *   glyph for a row with no identity to show). The slot occupies its width either way.
 * @param {string} [props.imageUrl] - Avatar source; absent falls back to initials, never an empty box
 * @param {string} [props.imageAlt] - Avatar alt text; defaults to `title`
 * @param {string} [props.href] - Destination for the avatar and the title. Without one, neither links.
 * @param {string} props.title - The row's name/headline
 * @param {string} [props.subtitleTitle] - Job title half of the subtitle sentence
 * @param {string} [props.subtitleCompanyName] - Company half of the subtitle sentence
 * @param {string} [props.subtitleCompanyHref] - Links the company half; plain text without one
 * @param {React.ReactNode} [props.tags] - Chip slot, right-justified on the title line (per the V2
 *   design's person rows) and hidden on xs, where the line has no room for it. The chips' own strip
 *   container still belongs to whatever produces them.
 * @param {React.ReactNode} [props.actions] - Trailing affordance slot (e.g. a row menu)
 */
function ListRow({
  leading = null,
  imageUrl = null,
  imageAlt = null,
  href = null,
  title,
  subtitleTitle = null,
  subtitleCompanyName = null,
  subtitleCompanyHref = null,
  tags = null,
  actions = null,
}) {
  const avatar = leading || (
    <ImageCropped roundedCircle src={imageUrl} alt={imageAlt || title} size={AVATAR_SIZE} fallbackToInitials />
  );

  // The company is a link only when the caller has somewhere to send it, and the " at " joiner
  // exists only when both halves do — a missing job title otherwise leaves a dangling "at Company".
  let company = null;
  if (subtitleCompanyName) {
    company = subtitleCompanyHref ? <a href={subtitleCompanyHref}>{subtitleCompanyName}</a> : subtitleCompanyName;
  }

  let subtitle = null;
  if (subtitleTitle && company) {
    subtitle = (
      <>
        {subtitleTitle} at {company}
      </>
    );
  } else if (subtitleTitle) {
    subtitle = subtitleTitle;
  } else if (company) {
    subtitle = company;
  }

  return (
    <div className="d-flex align-items-center">
      <div className="flex-shrink-0 me-2">{href ? <a href={href}>{avatar}</a> : avatar}</div>
      <div className="overflow-hidden">
        <div className="fw-semibold text-truncate">{href ? <a href={href}>{title}</a> : title}</div>
        {subtitle && <div className="text-muted small text-truncate">{subtitle}</div>}
      </div>
      {tags && <div className="ms-auto ps-2 flex-shrink-0 d-none d-sm-block">{tags}</div>}
      {actions && <div className={`ps-2 flex-shrink-0${tags ? "" : " ms-auto"}`}>{actions}</div>}
    </div>
  );
}

ListRow.propTypes = {
  leading: PropTypes.node,
  imageUrl: PropTypes.string,
  imageAlt: PropTypes.string,
  href: PropTypes.string,
  title: PropTypes.string.isRequired,
  subtitleTitle: PropTypes.string,
  subtitleCompanyName: PropTypes.string,
  subtitleCompanyHref: PropTypes.string,
  tags: PropTypes.node,
  actions: PropTypes.node,
};

export default ListRow;
